package it.neckar.runtime.context

import kotlinx.serialization.Serializable
import kotlin.jvm.JvmInline

/**
 * Represents a hostname
 */
@JvmInline
@Serializable
value class Hostname(val value: String) {
  init {
    require(value.isNotBlank()) { "Hostname must not be blank" }
    require(value.contains('@').not()) { "Hostname [$value] must not contain '@'" }
  }

  /**
   * The label before the first dot: `hafen-host.neckar.it` → `hafen-host`. What a deploy resource
   * names a host by when the domain is implied — a container name, a directory, a compose service.
   */
  val shortName: String
    get() = value.substringBefore('.')

  /**
   * The name a constant or a generated task takes for this host: `worker-01-host` → `Worker01Host`,
   * `dev.elektromeister.neckar.it` → `DevElektromeister`.
   * The `Host` part stays: `auth.neckar.it` is Keycloak, `auth-host.neckar.it` the machine under it.
   */
  val constantName: String
    get() = upperCamelCase(value.removeSuffix(".neckar.it"), '-', '.')

  /**
   * Whether the name resolves to the machine it is read on. RFC 6761 reserves `.localhost` for
   * exactly that, and local Traefik serves each product's worktree under a subdomain of it.
   */
  val namesLocalMachine: Boolean
    get() = value.equals(LocalhostName, ignoreCase = true) || value.endsWith(".$LocalhostName", ignoreCase = true)

  override fun toString(): String {
    return value
  }

  companion object {
    /**
     * Represents "localhost"
     */
    private const val LocalhostName = "localhost"

    val localhost: Hostname = Hostname(LocalhostName)

    fun nullable(hostnameAsString: String?): Hostname? {
      if (hostnameAsString == null) {
        return null
      }
      val trimmed = hostnameAsString.trim()
      if (trimmed.isEmpty()) {
        return null
      }
      return Hostname(trimmed)
    }
  }
}
